// @file Composable that abstracts getting the properties needed to pass to InteractiveAudioPlayer.vue
import { getDurationMetadataText } from '@@/bits/attachments'
import { LinkHelpers, type BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import { CORS, makeElvisProxyUrl } from '@padlet/elvis-client'
import { computed, type Ref } from 'vue'

export const useInteractiveAudioPlayerProps = ({
  attachmentUrl,
  attachmentSignedUrl,
  linkDisplayAttributes,
}: {
  attachmentUrl: Ref<string | null>
  attachmentSignedUrl: Ref<string | null>
  linkDisplayAttributes: Ref<BeethovenDisplayAttributes | null>
}): {
  isUploadedAudioAttachment: typeof isUploadedAudioAttachment
  audioSrc: typeof audioSrc
  waveformData: typeof waveformData
  audioDuration: typeof audioDuration
  audioCaptionsFileUrl: typeof audioCaptionsFileUrl
  displayedAudioDuration: typeof displayedAudioDuration
} => {
  const isUploadedAudioAttachment = computed<boolean>(
    () => linkDisplayAttributes.value != null && LinkHelpers.isUploadedAudio(linkDisplayAttributes.value),
  )

  const audioSrc = computed<string | null>(() => {
    // we use a special subdomain for uploaded audio attachments to allow range requests for
    // audio attachments, which are required for streaming audio and skipping ahead in the audio.
    // See: https://github.com/padlet/mozart/pull/34373
    if (linkDisplayAttributes.value != null && LinkHelpers.isUploadedAudio(linkDisplayAttributes.value)) {
      return linkDisplayAttributes.value.url
    }
    // If we have a signed URL, we can use it directly as the audio source.
    // The signed URL is public and served from our Rossini service.
    if (attachmentSignedUrl.value != null) {
      return attachmentSignedUrl.value
    }
    // Otherwise, route the URL through our Elvis proxy to avoid random issues like CORS.
    if (attachmentUrl.value != null) {
      return makeElvisProxyUrl(attachmentUrl.value, { cors: CORS.ALL, expiryInDays: 1 })
    }
    return null
  })

  const waveformData = computed<number[] | null>(() => {
    return linkDisplayAttributes.value?.metadata?.waveform_data ?? null
  })

  const audioDuration = computed<number | null>(() => {
    return linkDisplayAttributes.value?.metadata?.duration ?? null
  })

  const audioCaptionsFileUrl = computed<string | null>(() => {
    return linkDisplayAttributes.value?.metadata?.transcription_url ?? null
  })

  const displayedAudioDuration = computed<string>(() => {
    return linkDisplayAttributes.value != null ? getDurationMetadataText(linkDisplayAttributes.value) : ''
  })

  return {
    isUploadedAudioAttachment,
    audioSrc,
    waveformData,
    audioDuration,
    audioCaptionsFileUrl,
    displayedAudioDuration,
  }
}
